// src/features/atividade/mapa/estiloMapa.js
//
// O estilo do mapa da gravação: MapLibre GL sobre os basemaps da CARTO.
//
// ─── POR QUE NÃO O GOOGLE MAPS JS ───────────────────────────────────────────
//
// O Dynamic Maps do Google JS API cobra por carregamento de mapa. Uma corrida
// de 40 minutos com o app em tela é um carregamento; a tela de detalhe é
// outro; a lista de atividades, se usasse Static Maps, seria um por item. Vira
// custo variável preso ao uso, e o uso é justamente o que a feature quer
// incentivar. MapLibre é biblioteca, não serviço: o custo é o do tile.
//
// ─── POR QUE VETORIAL, E NÃO RASTER ─────────────────────────────────────────
//
// A CARTO está aposentando os PNG raster: hoje eles saem com marca d'água
// "API key required" quando a chave falta, e a documentação avisa que a
// exigência pode se estender ao vetorial. O estilo vetorial Dark Matter é o
// caminho que não vai quebrar sozinho — e ainda é mais nítido em tela retina,
// porque o texto e as linhas são desenhados no aparelho em vez de virem
// rasterizados a 256 px.
//
// ─── A CHAVE ────────────────────────────────────────────────────────────────
//
// `REACT_APP_CARTO_KEY` é uma chave PÚBLICA de tile: ela viaja na URL de todo
// tile, então qualquer pessoa com o DevTools aberto a lê. Não é segredo e não
// entra no Secret Manager — mas também não entra no código, porque muda por
// ambiente e porque commitá-la é o primeiro passo para ela virar permanente.
//
// SEM CHAVE, O MAPA NÃO É CARREGADO. Fundo escuro liso com o traçado por cima,
// e nada mais. É deliberado: um mapa com marca d'água repetida é pior que mapa
// nenhum, e um mapa que funciona hoje porque a CARTO ainda não cobra do
// vetorial é uma quebra agendada para uma data que ninguém escolheu.

/** Chave pública de tile da CARTO. Vazia = mapa desligado (ver acima). */
const CHAVE_CARTO = (process.env.REACT_APP_CARTO_KEY || '').trim();

/** Override cru de tiles raster. Tem precedência sobre tudo. */
const OVERRIDE_TILES = (process.env.REACT_APP_MAP_TILES_URL || '').trim();

const ESTILO_VETORIAL = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const RASTER_PADRAO = 'https://basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{ratio}.png';

/**
 * Atribuição. SEMPRE VISÍVEL — não é rodapé opcional, é a condição de uso dos
 * dados e do basemap. O brief a coloca a 372 px do fundo na tela de gravação;
 * quem posiciona é o componente, não este módulo.
 */
export const ATRIBUICAO = '© OpenStreetMap · © CARTO';

/** Versão curta, para onde o espaço é curto. Os dois créditos, sempre. */
export const ATRIBUICAO_CURTA = '© OSM · © CARTO';

/** Sem mapa carregado, creditar a CARTO seria crédito por serviço não usado. */
export const ATRIBUICAO_SEM_MAPA = '© OpenStreetMap';

const TAMANHO_TILE = 256;

/** "@2x" só quando o provedor e a tela pedem. */
function razao() {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  return dpr > 1.5 ? '@2x' : '';
}

/** A URL com a chave grudada, respeitando query string que já exista. */
function comChave(url) {
  if (!CHAVE_CARTO) return url;
  return `${url}${url.includes('?') ? '&' : '?'}key=${encodeURIComponent(CHAVE_CARTO)}`;
}

/**
 * `{s}` vira uma lista de URLs.
 *
 * O MapLibre não conhece o `{s}` do Leaflet: ele aceita um ARRAY de templates
 * e faz o rodízio sozinho. Expandir aqui é o que permite colar no
 * `REACT_APP_MAP_TILES_URL` uma URL no formato que todo provedor documenta.
 */
function expandirSubdominios(template) {
  if (!template.includes('{s}')) return [template];
  return ['a', 'b', 'c', 'd'].map((s) => template.replace('{s}', s));
}

/** Há basemap para carregar? */
export function temMapa() {
  return Boolean(CHAVE_CARTO || OVERRIDE_TILES);
}

/** Qual atribuição cabe no estado atual. */
export function atribuicaoAtual() {
  return temMapa() ? ATRIBUICAO_CURTA : ATRIBUICAO_SEM_MAPA;
}

/**
 * Estilo raster montado à mão: uma fonte, uma camada, e o fundo do app por
 * baixo. Usado só pelo override — o caminho normal é o vetorial.
 *
 * @param {string} template URL com {z}/{x}/{y} e, se quiser, {s} e {ratio}
 * @returns {object} style spec do MapLibre
 */
function estiloRaster(template) {
  const tiles = expandirSubdominios(comChave(template.replace('{ratio}', razao())));

  return {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles,
        tileSize: TAMANHO_TILE,
        minzoom: 0,
        maxzoom: 19,
        attribution: ATRIBUICAO,
      },
    },
    layers: [
      { id: 'fundo', type: 'background', paint: { 'background-color': '#0B0D12' } },
      { id: 'osm', type: 'raster', source: 'osm', paint: { 'raster-opacity': 1 } },
    ],
  };
}

/**
 * Estilo só de fundo, sem tile nenhum. É o que roda sem chave configurada.
 *
 * O mapa CONTINUA sendo um mapa: projeção real, câmera que segue, zoom
 * correto. O que falta é a rua desenhada por baixo — e o traçado sozinho sobre
 * o fundo da marca é legível, porque é o formato do percurso que se reconhece,
 * não o nome da avenida.
 *
 * Manter o MapLibre aqui em vez de cair num SVG estático evita um segundo modo
 * de renderização para manter em sincronia.
 *
 * @returns {object} style spec do MapLibre
 */
function estiloSemTiles() {
  return {
    version: 8,
    sources: {},
    layers: [{ id: 'fundo', type: 'background', paint: { 'background-color': '#0B0D12' } }],
  };
}

/**
 * O estilo que o MapLibre deve carregar.
 *
 * Três caminhos, nesta precedência:
 *  1. `REACT_APP_MAP_TILES_URL` — override cru, para trocar de provedor sem
 *     tocar em código;
 *  2. `REACT_APP_CARTO_KEY` — o caminho normal: Dark Matter vetorial;
 *  3. nada — fundo liso, sem tile e sem marca d'água.
 *
 * @returns {string|object} URL de estilo vetorial ou style spec
 */
export function estiloEscuro() {
  if (OVERRIDE_TILES) return estiloRaster(OVERRIDE_TILES);
  if (CHAVE_CARTO) return comChave(ESTILO_VETORIAL);
  return estiloSemTiles();
}

/** Cores do traçado — os mesmos valores dos tokens, aqui em literal porque o
 *  MapLibre pinta em canvas e não enxerga custom property de CSS. */
export const CORES_TRACADO = {
  contorno: '#0B0D12', // 10px por baixo, dá o vinco que separa do mapa
  linha: '#FBBD04', //  5px por cima  (primary)
  inicio: '#3ECF8E', //  success
  fim: '#F0564A', //  danger
  atual: '#3A55E0', //  accent
};

export default estiloEscuro;
